import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const sequentialVsParallelCode = `//  Sequential (Linear Execution) - ~6 seconds
async function loadSequential() {
    const user = await fetchUser();      // Wait 2s
    const posts = await fetchPosts();    // Wait 2s
    const meta = await fetchMeta();      // Wait 2s
    return { user, posts, meta };
}

// Parallel (Concurrent Execution) - ~2 seconds
async function loadParallel() {
    // Start all requests simultaneously
    const [user, posts, meta] = await Promise.all([
        fetchUser(),
        fetchPosts(),
        fetchMeta()
    ]);
    return { user, posts, meta };
}`;

const settledVsFastCode = `// 1. All-or-Nothing (Promise.all)
// Fails immediately if ANY request fails
const results = await Promise.all([p1, p2, p3]);

// 2. Resilient (Promise.allSettled)
// Returns status for every promise, never throws
const statuses = await Promise.allSettled([p1, p2, p3]);
const successful = statuses
    .filter(r => r.status === 'fulfilled')
    .map(r => r.value);`;

const concurrencyPoolingCode = `// --- Controlled Concurrency Pattern ---
async function processInBatches(items, limit = 5) {
    const results = [];
    for (let i = 0; i < items.length; i += limit) {
        const batch = items.slice(i, i + limit);
        const batchResults = await Promise.all(
            batch.map(item => processItem(item))
        );
        results.push(...batchResults);
    }
    return results;
}

// Process 100 images, but only 5 at a time
const allResults = await processInBatches(imageUrls, 5);`;

const JSParallel = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Concurrent Programming</div>
                <h1>Parallel & Sequential Execution</h1>
                <p className="hero-lead">
                    Mastering the timing of asynchronous operations is the difference
                    between a sluggish UI and a high-performance engine. Learn to
                    orchestrate complex data flows using concurrency and parallelism.
                </p>
            </div>

            <div className="content-section">
                <h2>The Async Waterfall Problem</h2>
                <p>
                    A common mistake in modern JavaScript is creating an "Async Waterfall."
                    This happens when you <code>await</code> multiple independent
                    operations in a series. If each takes 2 seconds, three operations
                    will take 6 seconds, even if they don't depend on each other.
                </p>
                <CodeBlock code={sequentialVsParallelCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Resilience vs. Speed</h2>
                <p>
                    Choosing the right orchestration method depends on your
                    <strong>Failure Model</strong>. If you need <em>all</em> the data
                    to render (like a bank transaction), <code>Promise.all</code> is correct.
                    If partial data is acceptable (like a dashboard with multiple
                    independent widgets), <code>Promise.allSettled</code> is the standard.
                </p>
                <CodeBlock code={settledVsFastCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Controlled Concurrency (Pooling)</h2>
                <p>
                    While <code>Promise.all</code> is fast, running 1,000 requests
                    simultaneously can crash a server or exhaust browser memory.
                    <strong>Concurrency Pooling</strong> allows you to process
                    large datasets in controlled "batches," maintaining speed
                    without overwhelming resources.
                </p>
                <CodeBlock code={concurrencyPoolingCode} language="javascript" />
            </div>

            <ProTip><p>
                    JavaScript is single-threaded for CPU tasks, but its I/O
                    (network, disk) is handled by the underlying system (Libuv).
                    When you run "Parallel" requests, you aren't multi-threading
                    your code; you are leveraging the browser's ability to handle
                    multiple network sockets simultaneously.
                </p></ProTip>

            <KeyFacts><ul>
                    <li>**Context:** Only use sequential <code>await</code> if Task B depends on Task A.</li>
                    <li>**Batching:** Use pooling for operations involving more than 10-20 concurrent requests.</li>
                    <li>**Resilience:** Favor <code>allSettled</code> for non-critical fallback data.</li>
                    <li>**Racing:** Use <code>Promise.race</code> for implementing timeouts or finding the fastest server.</li>
                    <li> **Anti-Pattern:** Never start a promise and don't await/catch it (creating a "floating" promise).</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSParallel;


